"use client";

import Arrow2LeftIcon from "@/app/_components/Icons/Arrow2LeftIcon";
import ArrowLeftIcon from "@/app/_components/Icons/ArrowLeftIcon";
import { NavigateBefore } from "@mui/icons-material";
import { Box, Breadcrumbs, Button, Container, Icon, Link, Stack, Typography } from "@mui/material";
import DOMPurify from "isomorphic-dompurify";
import NextLink from "next/link";
import { useEffect, useRef, useState } from "react";
import GoBackButton from "./GoBackButton";
export type BreadcrumbType = {
  title: string;
  href?: string;
};

type Props = {
  breadcrumbs?: BreadcrumbType[];
};
const crumbSx = {
  fontSize: { xs: "0.75rem", md: "1rem" },
  lineHeight: { xs: 1.7, md: 1.5 },
  fontWeight: 400,
  whiteSpace: "nowrap",
};

// degrees: the rtl style plugin rewrites the words left/right and would fade the wrong edge
const fadeMask = {
  none: "none",
  left: "linear-gradient(90deg, transparent, #000 1.75rem)",
  right: "linear-gradient(270deg, transparent, #000 1.75rem)",
  both: "linear-gradient(90deg, transparent, #000 1.75rem, #000 calc(100% - 1.75rem), transparent)",
};

function useScrollEdgeFade(itemCount: number) {
  const ref = useRef<HTMLDivElement>(null);
  const [fade, setFade] = useState<keyof typeof fadeMask>("none");

  useEffect(() => {
    const ol = ref.current?.querySelector("ol");
    if (!ol) return;

    const update = () => {
      const box = ol.getBoundingClientRect();
      const items = ol.querySelectorAll("li");
      const first = items[0];
      const last = items[items.length - 1];
      if (!first || !last) return;
      const start = first.getBoundingClientRect();
      const end = last.getBoundingClientRect();
      const left = end.left < box.left - 1;
      const right = start.right > box.right + 1;
      const next = left && right ? "both" : left ? "left" : right ? "right" : "none";
      setFade((prev) => (prev === next ? prev : next));
    };

    let alive = true;
    update();
    ol.addEventListener("scroll", update, { passive: true });
    const observer = new ResizeObserver(update);
    observer.observe(ol);
    document.fonts?.ready.then(() => {
      if (alive) update();
    });
    return () => {
      alive = false;
      ol.removeEventListener("scroll", update);
      observer.disconnect();
    };
  }, [itemCount]);

  return { ref, fade };
}

export default function PageHeaderBreadcrumbs(props: Props) {
  const { breadcrumbs } = props;
  const { ref, fade } = useScrollEdgeFade(breadcrumbs?.length ?? 0);

  const breadcrumbsList = breadcrumbs
    ? breadcrumbs.map((item, index) => {
        const isCurrent = !item.href || breadcrumbs.length - 1 === index;
        if (!isCurrent) {
          return (
            <Link component={NextLink} underline="hover" href={item.href} key={index} sx={{ color: { xs: "grey.800", md: "inherit" } }}>
              <Typography component="span" variant="inherit" sx={crumbSx}>
                {item.title}
              </Typography>
            </Link>
          );
        }
        return (
          <Typography component="span" variant="inherit" key={index} sx={{ ...crumbSx, color: "text.primary", fontWeight: { xs: 600, md: 400 } }}>
            {item.title}
          </Typography>
        );
      })
    : null;
  if (!breadcrumbsList || breadcrumbsList?.length <= 0) {
    return <></>;
  }
  return (
    <Breadcrumbs
      ref={ref}
      sx={{
        minWidth: 0,
        flex: { xs: 1, md: "unset" },
        whiteSpace: "nowrap",
        maskImage: fadeMask[fade],
        WebkitMaskImage: fadeMask[fade],
        "& .MuiBreadcrumbs-separator": { mx: { xs: 0.25, md: 1 } },
        "& ol": {
          alignItems: "center",
          overflowX: "auto",
          overflowY: "hidden",
          scrollbarWidth: "none",
          flexWrap: "nowrap !important",
        },
        "&::-webkit-scrollbar": {
          display: "none",
        },
      }}
      separator={<NavigateBefore sx={{ zIndex: 1, fontSize: { xs: 13, md: 16 }, color: "secondary.main" }} />}
      aria-label="breadcrumb"
    >
      {breadcrumbsList}
    </Breadcrumbs>
  );
}
